import React, { useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import TrustSection from '../components/TrustSection';
import { Toaster } from '../components/ui/sonner';
import { Button } from '../components/ui/button';
import { Radar, Building2, TreePine, Check, ArrowRight, Mail } from 'lucide-react';
import { getImageUrl } from '../lib/api';

const PastsLogistikaPage = () => {
  const contactRef = useRef(null);
  const location = useLocation();

  // Scroll to anchor on page load
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [location]);

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const problems = [
    "Pēdējās jūdzes piegāžu slodze un izmaksas reģionos",
    "Sūtījumu izsniegšana un atgriešana ārpus darba laika (24/7)",
    "Nepietiekama redzamība loģistikas plūsmās un atbildības strīdi",
    "Sarežģīta infrastruktūra daudzdzīvokļu ēkās (ieejas/podezdi)",
    "Piegādes ciemos un mazapdzīvotās vietās ar minimāliem būvdarbiem"
  ];

  const solutions = [
    {
      id: 1,
      slug: "izsekosana",
      icon: Radar,
      image: "logistics_tracking.webp",
      title: "Smalki sūtījumu izsekošanas risinājumi",
      description: "Kompakti izsekošanas risinājumi sūtījumu, maisu un loģistikas plūsmu pārraudzībai, īpaši piemēroti reģioniem un lauku teritorijām.",
      points: [
        "Ilgstoša autonomija",
        "Piemērots plūsmu pārraudzībai (nevis tikai vienam sūtījumam)",
        "Vienkārša ieviešana pilotprojektam"
      ]
    },
    {
      id: 2,
      slug: "daudzdzivoklu",
      icon: Building2,
      image: "apartment_locker.webp",
      title: "Autonomi pakuskapji daudzdzīvokļu ēkām",
      description: "Akumulatoru darbināti pakuskapji uzstādīšanai ēku ieejās (podezdos), kur nav pieejama pastāvīga elektrība vai sarežģīta infrastruktūra.",
      points: [
        "Bez sarežģītiem būvdarbiem",
        "Droša piekļuve iedzīvotājiem un kurjeriem",
        "Piemēroti vecām un jaunām ēkām"
      ]
    },
    {
      id: 3,
      slug: "regioni",
      icon: TreePine,
      image: "rural_delivery_point.webp",
      title: "Piegādes punkti ciemos un reģionos",
      description: "Autonomi pakuskapju punkti sūtījumu saņemšanai ciemos un mazapdzīvotās vietās, samazinot piegādes izmaksas un palielinot pieejamību.",
      points: [
        "Ātra uzstādīšana",
        "Piemēroti pašvaldībām un publiskām vietām",
        "Prognozējams uzturēšanas modelis"
      ]
    }
  ];

  const steps = [
    { number: 1, text: "Īsa vajadzību definīcija (vide, lietotāji, apjomi)" },
    { number: 2, text: "Pilotprojekts (ātra ieviešana, mērījumi, atgriezeniskā saite)" },
    { number: 3, text: "Konfigurācija un integrācijas (piekļuve, lietotāju plūsma, darbības loģika)" },
    { number: 4, text: "Uzturēšana un mērogošana (monitorings, serviss, paplašināšana)" }
  ];

  const securityPoints = [
    "Kontrolēta piekļuve un notikumu žurnāli",
    "Skaidrs uzturēšanas un servisa modelis",
    "Pielāgojams risinājums dažādām vidēm Latvijā"
  ];

  return (
    <>
      <Helmet>
        <title>Pasts un loģistika: pakuskapju risinājumi operatoriem | pakuskapis.lv</title>
        <meta name="description" content="Pakuskapju un izsekošanas risinājumi pasta un loģistikas operatoriem Latvijā: 24/7 izsniegšana, autonomie punkti, pilotprojekti un uzturēšana kopš 2019. gada." />
        <meta property="og:title" content="Pasts un loģistika: pakuskapju risinājumi operatoriem | pakuskapis.lv" />
        <meta property="og:description" content="Pakuskapju un izsekošanas risinājumi pasta un loģistikas operatoriem Latvijā: 24/7 izsniegšana, autonomie punkti, pilotprojekti un uzturēšana kopš 2019. gada." />
        <link rel="canonical" href="https://pakuskapis.lv/pasts-un-logistika" />
      </Helmet>

      <Header onCtaClick={scrollToContact} onCallClick={scrollToContact} />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6">
                Pakuskapju un izsekošanas risinājumi pasta un loģistikas operatoriem Latvijā
              </h1>
              <p className="text-lg lg:text-xl text-slate-600 leading-relaxed mb-8">
                Mēs palīdzam pasta un loģistikas operatoriem samazināt pēdējās jūdzes izmaksas un palielināt pieejamību, izmantojot autonomus pakuskapju punktus un pārdomātu sūtījumu plūsmu pārraudzību. Risinājumi ir pielāgoti reāliem ekspluatācijas apstākļiem Latvijā kopš 2019. gada.
              </p>
              <Button
                onClick={scrollToContact}
                className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-6 text-base"
              >
                <Mail className="w-5 h-5 mr-2" />
                Pieprasīt piedāvājumu
              </Button>
            </div>
          </div>
        </section>

        {/* Section 1 - Ko tas atrisina */}
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-8">
              Ko tas atrisina
            </h2>
            <div className="w-16 h-1 bg-slate-800 mb-10" />
            
            <ul className="space-y-4 max-w-3xl">
              {problems.map((problem, index) => (
                <li key={index} className="flex items-start space-x-4 p-4 bg-slate-50 rounded-lg">
                  <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-slate-700 text-lg">{problem}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Section 2 - Risinājumu bloki */}
        <section className="py-12 sm:py-16 lg:py-20 bg-slate-50" id="risinajumi">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-slate-900 mb-6 sm:mb-8">
              Risinājumu bloki
            </h2>
            <div className="w-12 sm:w-16 h-1 bg-slate-800 mb-8 sm:mb-12" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {solutions.map((solution) => {
                const Icon = solution.icon;
                return (
                  <div
                    key={solution.id}
                    id={solution.slug}
                    className="bg-white rounded-xl lg:rounded-2xl overflow-hidden border border-slate-100 hover:border-slate-200 hover:shadow-lg transition-all duration-300 scroll-mt-24"
                  >
                    {/* Image */}
                    <div className="h-40 sm:h-48 lg:h-56 overflow-hidden">
                      <img
                        src={getImageUrl(solution.image)}
                        alt={solution.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    </div>

                    <div className="p-4 sm:p-6 lg:p-8">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-slate-800 rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-5 -mt-10 sm:-mt-12 relative z-10 shadow-lg">
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
                      </div>

                      <h3 className="text-base sm:text-lg lg:text-xl font-bold text-slate-900 mb-2 sm:mb-3">
                        {solution.title}
                      </h3>

                      <p className="text-slate-600 text-sm lg:text-base leading-relaxed mb-4 sm:mb-5">
                        {solution.description}
                      </p>

                      <ul className="space-y-1.5 sm:space-y-2">
                        {solution.points.map((point, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className="text-xs sm:text-sm text-slate-700">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Section 3 - Kā mēs ieviešam */}
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-8">
              Kā mēs ieviešam
            </h2>
            <div className="w-16 h-1 bg-slate-800 mb-12" />

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step) => (
                <div key={step.number} className="relative">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-xl">{step.number}</span>
                    </div>
                    <p className="text-slate-700 text-base lg:text-lg leading-relaxed pt-2">
                      {step.text}
                    </p>
                  </div>
                  {step.number < 4 && (
                    <div className="hidden lg:block absolute top-6 left-16 w-full h-px bg-slate-200" style={{ width: 'calc(100% - 4rem)' }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 4 - Drošība un atbilstība */}
        <section className="py-16 lg:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-8">
                Drošība un atbilstība
              </h2>
              <div className="w-16 h-1 bg-slate-800 mb-8" />

              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Mēs strādājam ar publiskām un privātām organizācijām, kur drošība un caurspīdīga uzturēšana ir kritiski svarīga. Piekļuve, notikumu žurnāli un uzturēšanas procesi ir definēti un pārskatāmi.
              </p>

              <ul className="space-y-3">
                {securityPoints.map((point, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-lg">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Section 5 - Trust Section (reused from home) */}
        <TrustSection />

        {/* Contact Form */}
        <div ref={contactRef}>
          <ContactForm />
        </div>
      </main>

      <Footer />
      <Toaster position="top-right" />
    </>
  );
};

export default PastsLogistikaPage;
